import {
    EXPENSE_CATEGORIES,
    type ExpenseCategory,
    getCategoryLabel,
    isExpenseCategory,
} from "./categories";

/** User-facing mental-model groups. Stored category keys stay unchanged. */
export type CategoryGroupId =
    | "food"
    | "mobility"
    | "home"
    | "shopping"
    | "lifestyle"
    | "work"
    | "financial"
    | "review";

export interface CategoryGroup {
    id: CategoryGroupId;
    categories: readonly ExpenseCategory[];
}

/** Categories excluded from lifestyle spending recommendations. */
export const FINANCIAL_CATEGORIES: readonly ExpenseCategory[] = [
    "Fees",
    "Taxes",
    "Transfers",
    "Adjustments",
    "Investments",
];

export const REVIEW_CATEGORIES: readonly ExpenseCategory[] = ["Other"];

/**
 * Ecommerce = marketplace/online platform; Retail = physical/general goods.
 * Home & Services = utilities, repairs, home maintenance.
 * Services = professional/personal services (not utilities).
 */
export const CATEGORY_GROUPS: readonly CategoryGroup[] = [
    { id: "food", categories: ["Groceries", "Restaurants", "Delivery"] },
    { id: "mobility", categories: ["Transport", "Travel"] },
    { id: "home", categories: ["Home & Services", "Personal Care", "Services"] },
    { id: "shopping", categories: ["Ecommerce", "Retail"] },
    { id: "lifestyle", categories: ["Entertainment", "Health", "Subscriptions"] },
    { id: "work", categories: ["Education", "Tools", "Office"] },
    { id: "financial", categories: [...FINANCIAL_CATEGORIES] },
    { id: "review", categories: [...REVIEW_CATEGORIES] },
];

const GROUPED_SET = new Set(CATEGORY_GROUPS.flatMap((g) => g.categories));

/** Ensures every known category appears in exactly one group. */
export function validateCategoryGroups(): void {
    for (const cat of EXPENSE_CATEGORIES) {
        if (!GROUPED_SET.has(cat)) {
            throw new Error(`Category "${cat}" is missing from CATEGORY_GROUPS`);
        }
    }
}

export function isFinancialCategory(category: string | undefined): boolean {
    return Boolean(category && (FINANCIAL_CATEGORIES as readonly string[]).includes(category));
}

export function isReviewCategory(category: string | undefined): boolean {
    return !category?.trim() || category === "Other";
}

export function getCategoryGroupId(category: ExpenseCategory): CategoryGroupId | null {
    for (const group of CATEGORY_GROUPS) {
        if (group.categories.includes(category)) return group.id;
    }
    return null;
}

export interface GroupedCategoryOption {
    groupId: CategoryGroupId;
    category: ExpenseCategory;
    label: string;
    isSuggested: boolean;
}

type TFunction = (key: string) => string;

/**
 * Flat list of categories for pickers: suggested first, then grouped with financial/review last.
 */
export function buildGroupedCategoryOptions(
    t: TFunction,
    suggestedCategory?: string,
): GroupedCategoryOption[] {
    const suggested = suggestedCategory?.trim();
    const options: GroupedCategoryOption[] = [];

    const pushGroup = (group: CategoryGroup) => {
        const cats = [...group.categories];
        if (suggested && cats.includes(suggested as ExpenseCategory)) {
            cats.sort((a, b) => (a === suggested ? -1 : b === suggested ? 1 : 0));
        }
        for (const category of cats) {
            options.push({
                groupId: group.id,
                category,
                label: getCategoryLabel(category, t),
                isSuggested: category === suggested,
            });
        }
    };

    const primary = CATEGORY_GROUPS.filter((g) => g.id !== "financial" && g.id !== "review");
    const tail = CATEGORY_GROUPS.filter((g) => g.id === "financial" || g.id === "review");

    for (const group of primary) pushGroup(group);
    for (const group of tail) pushGroup(group);

    if (suggested && isExpenseCategory(suggested)) {
        options.sort((a, b) => {
            if (a.isSuggested && !b.isSuggested) return -1;
            if (!a.isSuggested && b.isSuggested) return 1;
            return 0;
        });
    }

    return options;
}

export function groupLabel(groupId: CategoryGroupId, t: TFunction): string {
    return t(`categoryGroups.${groupId}`);
}
